import { createSlice } from "@reduxjs/toolkit";
import { organizationProfileApi } from "Services/OrganizationProfileService";
import { letterHeadApi } from "Services/LetterHeadService";

export const initialState = {
    completedStepIdList: [],
    bnein: "",
    links: {
        website: "",
        facebook: "",
        linkedin: "",
        instagram: "",
        x: "",
    },
    letterHead: {
        header: {
            previewBlob: null,
            previewUrl: "",
            headerImageId: "",
        },
        footer: {
            previewBlob: null,
            previewUrl: "",
            footerImageId: "",
        },
    },
    missionStatement: "",
    categories: [],
    populationsServed: [],
    gasServed: [],
    sdgServed: [],
    similarCharities: [],
    saving: false,
    otherInfoLoaded: false,
    gasServedAreaType: "specific", // "specific" or "entire"
    gasServedSelectedAreas: {
        selectedCountries: [],
        selectedRegions: [],
        selectedCounties: [],
        selectedCities: [],
    },
    errors: {
        mission: "",
    }
};

const organizationStepperSlice = createSlice({
    name: "organization-stepper",
    initialState,
    reducers: {
        addStepIdToCompletedList: (state, action) => {
            const stepId = action.payload;
            if (!state.completedStepIdList.includes(stepId)) {
                state.completedStepIdList.push(action.payload);
            }
        },
        removeStepIdToCompletedList: (state, action) => {
            state.completedStepIdList = state.completedStepIdList.filter(
                (stepId) => stepId !== action.payload
            );
        },
        setBnEin: (state, action) => {
            state.bnein = action.payload;
        },
        setLinks: (state, action) => {
            const { type, value } = action.payload;
            state.links[type] = value;
        },
        setLetterHead: (state, action) => {
            const { type, value } = action.payload;
            state.letterHead[type] = value;
        },
        clearLetterHead: (state, action) => {
            const type = action.payload;
            state.letterHead[type] = initialState.letterHead[type];
        },
        setMissionStatement: (state, action) => {
            state.missionStatement = action.payload;
        },
        setCategories: (state, action) => {
            state.categories = action.payload;
        },
        setPopulationServed: (state, action) => {
            state.populationsServed = action.payload;
        },
        setGasServed: (state, action) => {
            state.gasServed = action.payload;
        },
        setSdgServed: (state, action) => {
            state.sdgServed = action.payload;
        },
        setSimilarCharities: (state, action) => {
            state.similarCharities = action.payload;
        },
        setGasServedSelectedAreas: (state, action) => {
            const { key, value } = action.payload;
            state.gasServedSelectedAreas[key] = value;
        },
        setGasServedAreaType: (state, action) => {
            state.gasServedAreaType = action.payload;
        },
        clearGasServedSelectedAreas: (state) => {
            state.gasServedAreaType = "specific";
            state.gasServedSelectedAreas = {
                selectedCountries: [],
                selectedRegions: [],
                selectedCounties: [],
                selectedCities: [],
            };
        },
        setErrorFields: (state, action) => {
            Object.entries(action.payload).forEach(([key, value]) => {
                state.errors[key] =  value;
            });
        }
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                organizationProfileApi.endpoints.updateOrganizationProfile
                    .matchPending,
                (state) => {
                    state.saving = true;
                }
            )
            .addMatcher(
                organizationProfileApi.endpoints.updateOrganizationProfile
                    .matchFulfilled,
                (state) => {
                    state.saving = false;
                }
            )
            .addMatcher(
                organizationProfileApi.endpoints.updateOrganizationProfile
                    .matchRejected,
                (state) => {
                    state.saving = false;
                }
            )
            .addMatcher(
                organizationProfileApi.endpoints.getOrganizationOtherInfo
                    .matchFulfilled,
                (state, { payload }) => {
                    state.otherInfoLoaded = true;
                    state.bnein = payload.businessEin;
                    state.links.website = payload.website;
                    state.links.facebook =
                        payload.socialMedia?.find(
                            (s) => s.socialMediaType === 0
                        )?.website ?? "";
                    state.links.linkedin =
                        payload.socialMedia?.find(
                            (s) => s.socialMediaType === 1
                        )?.website ?? "";
                    state.links.instagram =
                        payload.socialMedia?.find(
                            (s) => s.socialMediaType === 2
                        )?.website ?? "";
                    state.links.x =
                        payload.socialMedia?.find(
                            (s) => s.socialMediaType === 3
                        )?.website ?? "";
                    state.missionStatement = payload.mission;
                    state.categories = payload.categories;
                    state.populationsServed = payload.populationsServed;
                    state.gasServed =
                        payload.geographicAreasServed
                            ?.map((area) => {
                                try {
                                    return JSON.parse(area);
                                } catch (error) {
                                    console.error(
                                        "Failed to parse individual area string:",
                                        area,
                                        error
                                    );
                                    return null;
                                }
                            })
                            .filter(Boolean) ?? [];
                    state.sdgServed =
                        payload.unSdgGoals?.map((g) => parseInt(g)) ?? [];
                    state.similarCharities =
                        payload.similarCharities?.map((charity) => ({
                            ein: charity.ein,
                            label: charity.name,
                            city: charity.city,
                            state: charity.state,
                            county: charity.county,
                            nteeCode: charity.nteeCode,
                            nteeTitle: charity.nteeTitle,
                        })) ?? [];
                }
            )
            .addMatcher(
                letterHeadApi.endpoints.getLetterHead2.matchFulfilled,
                (state, { payload }) => {
                    state.letterHead.header.previewUrl = payload.headerImageUrl;
                    state.letterHead.header.headerImageId =
                        payload.headerImageId;
                    state.letterHead.footer.previewUrl = payload.footerImageUrl;
                    state.letterHead.footer.footerImageId =
                        payload.footerImageId;
                }
            );
    },
});

const organizationStepperState = (state) => state.organizationStepperState;

export const selectCompletedStepIdList = (state) =>
    organizationStepperState(state).completedStepIdList;
export const selectBnEin = (state) => organizationStepperState(state).bnein;
export const selectLinks = (state) => organizationStepperState(state).links;
export const selectLetterhead = (state) =>
    organizationStepperState(state).letterHead;
export const selectMissionStatement = (state) =>
    organizationStepperState(state).missionStatement;
export const selectCategories = (state) =>
    organizationStepperState(state).categories;
export const selectPopulationsServed = (state) =>
    organizationStepperState(state).populationsServed;
export const selectGasServed = (state) =>
    organizationStepperState(state).gasServed;
export const selectSdgServed = (state) =>
    organizationStepperState(state).sdgServed;
export const selectSimilarCharities = (state) =>
    organizationStepperState(state).similarCharities;
export const selectOtherInfoLoaded = (state) =>
    organizationStepperState(state).otherInfoLoaded;
export const selectFieldError = (state) =>
    organizationStepperState(state).errors;
export const selectGasServedAreaType = (state) =>
    organizationStepperState(state).gasServedAreaType;
export const selectGasServedSelectedAreas = (state) =>
    organizationStepperState(state).gasServedSelectedAreas;
export const selectGasServedHasSelectedAreas = (state) => {
    const selectedType = organizationStepperState(state).gasServedAreaType;
    const selectedAreas = organizationStepperState(state).gasServedSelectedAreas;

    return selectedType === "entire"
        ? (selectedAreas.selectedCountries?.length ?? 0) > 0
        : //If selectedType is specific, then check if any regions, counties, or cities are selected
          (selectedAreas.selectedRegions?.length ?? 0) > 0 ||
              (selectedAreas.selectedCounties?.length ?? 0) > 0 ||
              (selectedAreas.selectedCities?.length ?? 0) > 0;
};

export const {
    addStepIdToCompletedList,
    removeStepIdToCompletedList,
    setBnEin,
    setLinks,
    setLetterHead,
    clearLetterHead,
    setMissionStatement,
    setCategories,
    setPopulationServed,
    setGasServed,
    setSdgServed,
    setSimilarCharities,
    setGasServedSelectedAreas,
    setGasServedAreaType,
    clearGasServedSelectedAreas,
    setErrorFields,
} = organizationStepperSlice.actions;

export default organizationStepperSlice.reducer;
